import React from 'react'
import * as echarts from 'echarts'
import { Logger } from 'wdc-cube'
import { makeStyles } from 'tss-react/mui'
import { classToFComponent, inputSx, LifesHubColors } from 'src/utils/views'
import { TcmCard } from 'src/utils/views/modules/the-consumer-market'

import MenuItem from '@mui/material/MenuItem'
import TextField from '@mui/material/TextField'

import { EChartViewClass } from '../../the-consumer-market/views/helpers/base-echart'
import {
    axisLabelStyle,
    axisLineStyle,
    ChartFontSize,
    ChartPalette,
    splitLineStyle,
    tooltipStyle
} from '../../the-consumer-market/views/helpers/tcm_chart_theme'
import { GraficoScope, type FormatoGrafico } from '../ad_scopes'
import type { Indicador } from '../ad_types'
import { formatCompacto, formatMoeda, formatPercentual } from '../ad_utils'

const LOG = Logger.get('AD-Grafico')

const CORES = [
    LifesHubColors.chartEmphasis,
    LifesHubColors.blue,
    LifesHubColors.purple,
    LifesHubColors.muted2,
    LifesHubColors.green800,
    '#1e4e91',
    '#8f2020',
    '#245a27',
    '#6d4605',
    LifesHubColors.ink
]

const useStyles = makeStyles()({
    body: {
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        flexGrow: 1,
        minHeight: 0
    },
    chart: {
        display: 'flex',
        flexGrow: 1,
        flexBasis: 0,
        minHeight: 0,
        overflowX: 'hidden',
        overflowY: 'hidden'
    }
})

function rotulo(formato: FormatoGrafico, v: number) {
    switch (formato) {
        case 'percentual':
            return formatPercentual(v).replace(' %', '%')
        case 'indice':
            return new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 2 }).format(v)
        default:
            return formatCompacto(v)
    }
}

function completo(formato: FormatoGrafico, v: number) {
    switch (formato) {
        case 'percentual':
            return formatPercentual(v, 2)
        case 'indice':
            return new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 4 }).format(v)
        default:
            return formatMoeda(v)
    }
}

function tendencia(scope: GraficoScope): (number | null)[] {
    let n = 0
    let sx = 0
    let sy = 0
    let sxy = 0
    let sxx = 0
    for (const serie of scope.series) {
        serie.valores.forEach((v, x) => {
            if (v === null || v === undefined) return
            n++
            sx += x
            sy += v
            sxy += x * v
            sxx += x * x
        })
    }
    const den = n * sxx - sx * sx
    if (n < 2 || den === 0) return []
    const b = (n * sxy - sx * sy) / den
    const a = (sy - b * sx) / n
    return scope.datas.map((_, x) => a + b * x)
}

function buildChartData(scope: GraficoScope): echarts.EChartsOption {
    const formato = scope.formato
    const tend = tendencia(scope)
    return {
        color: CORES,
        tooltip: {
            ...tooltipStyle,
            trigger: 'axis',
            valueFormatter: (value) => (typeof value === 'number' ? completo(formato, value) : '—')
        },
        legend: {
            top: 0,
            left: 0,
            right: 0,
            icon: 'circle',
            itemHeight: 8,
            itemWidth: 8,
            textStyle: { color: ChartPalette.label, fontSize: ChartFontSize.axis },
            data: scope.series.map((s) => s.nome)
        },
        grid: { left: 8, right: 16, top: 30 + 20 * Math.ceil(scope.series.length / 2), bottom: 4, containLabel: true },
        xAxis: {
            type: 'category',
            boundaryGap: false,
            data: scope.datas,
            axisTick: { show: false },
            axisLine: axisLineStyle,
            axisLabel: axisLabelStyle
        },
        yAxis: {
            type: 'value',
            splitLine: splitLineStyle,
            axisLabel: { ...axisLabelStyle, formatter: (v: number) => rotulo(formato, v) }
        },
        series: [
            ...scope.series.map((s): echarts.SeriesOption => ({
                name: s.nome,
                type: 'line',
                data: s.valores,
                symbol: 'circle',
                symbolSize: 5,
                connectNulls: false,
                lineStyle: { width: 2 },
                areaStyle: { opacity: 0.18 },
                label: {
                    show: true,
                    position: 'top',
                    fontSize: 10,
                    color: LifesHubColors.muted,
                    formatter: (p) => (typeof p.value === 'number' ? rotulo(formato, p.value) : '')
                },
                labelLayout: { hideOverlap: true }
            })),
            ...(tend.length
                ? [
                      {
                          name: 'Tendência',
                          type: 'line',
                          data: tend,
                          symbol: 'none',
                          silent: true,
                          tooltip: { show: false },
                          lineStyle: { type: 'dashed', width: 2, color: LifesHubColors.ink },
                          z: 5
                      } as echarts.SeriesOption
                  ]
                : [])
        ]
    }
}

type GraficoViewProps = {
    className?: string
    scope: GraficoScope
}

class GraficoViewClass extends EChartViewClass<GraficoViewProps> {
    scope!: GraficoScope
    classes!: ReturnType<typeof useStyles>['classes']
    private __chave = ''

    onSyncState(props: GraficoViewProps) {
        this.scope = props.scope
        this.classes = useStyles().classes
        this.chartData = buildChartData(this.scope)
        this.loaded = this.scope.loaded
        const chave = `${this.scope.indicador}|${this.scope.series.map((s) => s.nome).join('|')}`
        if (this.chart && chave !== this.__chave) this.chart.clear()
        this.__chave = chave
        super.onSyncState(props)
    }

    readonly emitIndicadorChange = (evt: React.ChangeEvent<HTMLInputElement>) => {
        this.scope.onIndicadorChange(evt.target.value as Indicador).catch(LOG.caught)
    }

    override render({ className, scope }: GraficoViewProps): React.JSX.Element {
        const { classes } = this
        return (
            <TcmCard className={className} caption={scope.caption}>
                <div className={classes.body}>
                    <TextField
                        select
                        size="small"
                        value={scope.indicador}
                        onChange={this.emitIndicadorChange}
                        sx={inputSx}
                        aria-label="Indicador"
                    >
                        {scope.opcoes.map((o) => (
                            <MenuItem key={o.value} value={o.value}>
                                {o.label}
                            </MenuItem>
                        ))}
                    </TextField>
                    {super.render({ className: classes.chart, scope })}
                </div>
            </TcmCard>
        )
    }
}

export const GraficoView = classToFComponent(GraficoViewClass, React)
